import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { ReportTheme } from '../../types';
import { getChartColor } from './chartUtils';

interface DevicePerformancePieChartWidgetProps {
  data: any;
  data_points?: string[];
  report_start_date?: string;
  report_end_date?: string;
  reportTheme: ReportTheme;
}

export const DevicePerformancePieChartWidget: React.FC<DevicePerformancePieChartWidgetProps> = ({ 
  data, 
  data_points,
  report_start_date,
  report_end_date,
  reportTheme 
}) => {
  console.log('DevicePerformancePieChartWidget raw data:', data);

  // Process the raw API data internally for Device Performance
  const processData = () => {
    if (!data || !data.data || typeof data.data !== 'object') {
      console.warn('DevicePerformancePieChartWidget: Invalid data structure');
      return null;
    }

    const rawData = data.data;
    const dateEntries = Object.entries(rawData);
    
    if (dateEntries.length === 0) {
      console.warn('DevicePerformancePieChartWidget: No data entries found');
      return null;
    }

    // Aggregate device data across all dates
    const deviceTotals: { [key: string]: number } = {};

    dateEntries.forEach(([dateKey, dayData]: [string, any]) => {
      if (!dayData || typeof dayData !== 'object') {
        return;
      }

      // Process each device in the day's data (exclude browser data)
      Object.entries(dayData).forEach(([deviceKey, deviceData]: [string, any]) => {
        // Only process device types (mobile, desktop, tablet) and exclude browser data
        if ((deviceKey === 'mobile' || deviceKey === 'desktop' || deviceKey === 'tablet') && 
            deviceData && typeof deviceData === 'object') {
          const sessions = deviceData.sessions || deviceData.count || 0;
          deviceTotals[deviceKey] = (deviceTotals[deviceKey] || 0) + sessions;
        }
      });
    });

    if (Object.keys(deviceTotals).length === 0) {
      console.warn('DevicePerformancePieChartWidget: No device data found');
      return null;
    }
    
    const chartData = Object.entries(deviceTotals)
      .map(([deviceKey, sessions], index) => ({
        name: deviceKey.charAt(0).toUpperCase() + deviceKey.slice(1), // Capitalize first letter
        value: sessions,
        color: getChartColor(index, reportTheme.theme)
      }))
      .sort((a, b) => b.value - a.value); // Sort by sessions descending

    return chartData.length > 0 ? chartData : null;
  };

  const processedData = processData();

  if (!processedData || processedData.length === 0) {
    return null;
  }

  return (
    <ResponsiveContainer width="100%" height="100%">
      <PieChart>
        <Pie
          data={processedData}
          cx="50%"
          cy="50%"
          labelLine={false}
          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
          outerRadius={80}
          fill="#8884d8"
          dataKey="value"
        >
          {processedData.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={entry.color} />
          ))}
        </Pie>
        <Tooltip />
        <Legend />
      </PieChart>
    </ResponsiveContainer>
  );
};
